// Video metrics utility functions

// Function to update video metrics
export const createUpdateMetrics = (
    playerRef,
    hlsInstanceRef,
    dashPlayerRef,
    lastFramesRef,
    lastTimeRef,
    selectedVideoUrl,
    hlsUrl,
    dashUrl,
    fileUrl,
    setStreamInfo,
    setPlaybackMetrics,
    setChartLabels
) => {
    return () => {
        if (!playerRef.current) return;
        
        try {
            const player = playerRef.current;
            const internalPlayer = player.getInternalPlayer();
            if (!internalPlayer) return;

            // Get video element
            let videoElement = null;
            if (internalPlayer.tagName === 'VIDEO') {
                videoElement = internalPlayer;
            } else {
                videoElement = internalPlayer.querySelector?.('video');
            }
            
            if (!videoElement || videoElement.tagName !== 'VIDEO') return;

            // Get video properties
            const videoWidth = videoElement.videoWidth || 0;
            const videoHeight = videoElement.videoHeight || 0;
            const currentResolution = videoWidth && videoHeight ? `${videoWidth}x${videoHeight}` : '–';
            
            // Get buffered time
            const buffered = videoElement.buffered;
            const bufferLength = buffered.length;
            let bufferTime = '–';
            if (bufferLength > 0) {
                const endBuffer = buffered.end(bufferLength - 1);
                const currentTime = videoElement.currentTime || 0;
                bufferTime = `${(endBuffer - currentTime).toFixed(1)}`;
            }
            
            // Calculate FPS
            const now = performance.now();
            let fps = 0;
            const q = videoElement.getVideoPlaybackQuality ? videoElement.getVideoPlaybackQuality() : null;
            if (q && q.totalVideoFrames !== undefined) {
                const frameDelta = q.totalVideoFrames - lastFramesRef.current;
                const timeDelta = (now - lastTimeRef.current) / 1000;
                if (timeDelta > 0) {
                    fps = frameDelta / timeDelta;
                }
                lastFramesRef.current = q.totalVideoFrames;
                lastTimeRef.current = now;
            }

            // Get current video format
            const currentVideoUrl = selectedVideoUrl || hlsUrl || dashUrl || fileUrl;
            const currentFormat = selectedVideoUrl === hlsUrl ? 'HLS' : selectedVideoUrl === dashUrl ? 'DASH' : 
                                 hlsUrl ? 'HLS' : dashUrl ? 'DASH' : 'Direct';

            let allBitrates = [];
            let allResolutions = [];
            let currentBitrate = 0;
            let bandwidth = 0;
            let segmentLength = '6'; // Default segment length, can be extracted from stream if available

            // Try to get HLS/DASH specific metrics
            // ReactPlayer uses hls.js and dash.js directly, not video.js
            if (currentFormat === 'HLS' || currentFormat === 'DASH') {
                try {
                    // For HLS - ReactPlayer uses hls.js
                    if (currentFormat === 'HLS' && window.Hls) {
                        // Try to find hls.js instance - use captured ref first, then try other methods
                        let hlsInstance = hlsInstanceRef.current;
                        
                        if (!hlsInstance) {
                            // Check various ways hls.js might be attached
                            if (videoElement.__hls) {
                                hlsInstance = videoElement.__hls;
                                hlsInstanceRef.current = hlsInstance;
                            } else if (videoElement.hls) {
                                hlsInstance = videoElement.hls;
                                hlsInstanceRef.current = hlsInstance;
                            } else {
                                // Try to find it in the player's internal state
                                const internalPlayer = player.getInternalPlayer();
                                if (internalPlayer) {
                                    if (internalPlayer.__hls) {
                                        hlsInstance = internalPlayer.__hls;
                                        hlsInstanceRef.current = hlsInstance;
                                    } else if (internalPlayer.hls) {
                                        hlsInstance = internalPlayer.hls;
                                        hlsInstanceRef.current = hlsInstance;
                                    } else if (internalPlayer.player && internalPlayer.player.hls) {
                                        hlsInstance = internalPlayer.player.hls;
                                        hlsInstanceRef.current = hlsInstance;
                                    }
                                }
                                
                                // Also check ReactPlayer's internal state
                                if (!hlsInstance && player.player) {
                                    const rpPlayer = player.player;
                                    if (rpPlayer.hls) {
                                        hlsInstance = rpPlayer.hls;
                                        hlsInstanceRef.current = hlsInstance;
                                    } else if (rpPlayer.__hls) {
                                        hlsInstance = rpPlayer.__hls;
                                        hlsInstanceRef.current = hlsInstance;
                                    }
                                }
                            }
                        }
                        
                        if (hlsInstance) {
                            // console.log('HLS instance found:', {
                            //     hasLevels: !!hlsInstance.levels,
                            //     levelsLength: hlsInstance.levels?.length,
                            //     currentLevel: hlsInstance.currentLevel,
                            //     loadLevel: hlsInstance.loadLevel,
                            //     keys: Object.keys(hlsInstance)
                            // });
                            
                            if (hlsInstance.levels && hlsInstance.levels.length > 0) {
                                const levels = hlsInstance.levels;
                                allBitrates = [...new Set(levels.map(level => (level.bitrate / 1000000).toFixed(2)))];
                                allResolutions = [...new Set(levels.map(level => {
                                    if (level.width && level.height) {
                                        return `${level.width}x${level.height}`;
                                    }
                                    return null;
                                }).filter(Boolean))];
                                
                                const currentLevelIndex = hlsInstance.currentLevel !== -1 ? hlsInstance.currentLevel : hlsInstance.loadLevel;
                                if (currentLevelIndex !== -1 && currentLevelIndex < levels.length && levels[currentLevelIndex]) {
                                    const currentLevel = levels[currentLevelIndex];
                                    currentBitrate = parseFloat((currentLevel.bitrate / 1000000).toFixed(2));
                                    bandwidth = currentBitrate * 1.2; // Estimate bandwidth
                                    
                                    // Get segment duration from level or manifest
                                    if (currentLevel.duration) {
                                        segmentLength = currentLevel.duration.toFixed(1);
                                    } else if (hlsInstance.config && hlsInstance.config.maxBufferLength) {
                                        segmentLength = hlsInstance.config.maxBufferLength.toFixed(1);
                                    }
                                }
                                
                                // Also try to get bandwidth stats
                                if (hlsInstance.bandwidthEstimate) {
                                    bandwidth = parseFloat((hlsInstance.bandwidthEstimate / 1000000).toFixed(2));
                                }
                            } else {
                                // console.log('HLS instance found but no levels available yet');
                            }
                        } else {
                            // console.log('HLS instance not found');
                        }
                    }
                    
                    // For DASH - ReactPlayer uses dash.js
                    if (currentFormat === 'DASH' && window.dashjs) {
                        let dashPlayer = dashPlayerRef.current;
                        
                        if (!dashPlayer) {
                            // Check various ways dash.js might be attached
                            if (videoElement.__dashjs) {
                                dashPlayer = videoElement.__dashjs;
                                dashPlayerRef.current = dashPlayer;
                            } else if (videoElement.dashjs) {
                                dashPlayer = videoElement.dashjs;
                                dashPlayerRef.current = dashPlayer;
                            } else {
                                // Try to find it in the player's internal state
                                const internalPlayer = player.getInternalPlayer();
                                if (internalPlayer) {
                                    if (internalPlayer.__dashjs) {
                                        dashPlayer = internalPlayer.__dashjs;
                                        dashPlayerRef.current = dashPlayer;
                                    } else if (internalPlayer.dashjs) {
                                        dashPlayer = internalPlayer.dashjs;
                                        dashPlayerRef.current = dashPlayer;
                                    } else if (internalPlayer.player && internalPlayer.player.dashjs) {
                                        dashPlayer = internalPlayer.player.dashjs;
                                        dashPlayerRef.current = dashPlayer;
                                    }
                                }
                                
                                // Also check ReactPlayer's internal state
                                if (!dashPlayer && player.player) {
                                    const rpPlayer = player.player;
                                    if (rpPlayer.dashjs) {
                                        dashPlayer = rpPlayer.dashjs;
                                        dashPlayerRef.current = dashPlayer;
                                    } else if (rpPlayer.__dashjs) {
                                        dashPlayer = rpPlayer.__dashjs;
                                        dashPlayerRef.current = dashPlayer;
                                    }
                                }
                            }
                        }
                        
                        if (dashPlayer) {
                            try {
                                const bitrateInfoList = dashPlayer.getBitrateInfoListFor('video');
                                if (bitrateInfoList && bitrateInfoList.length > 0) {
                                    allBitrates = [...new Set(bitrateInfoList.map(info => (info.bitrate / 1000000).toFixed(2)))];
                                    allResolutions = [...new Set(bitrateInfoList.map(info => {
                                        if (info.width && info.height) {
                                            return `${info.width}x${info.height}`;
                                        }
                                        return null;
                                    }).filter(Boolean))];
                                    
                                    const quality = dashPlayer.getQualityFor('video');
                                    if (quality !== -1 && quality < bitrateInfoList.length) {
                                        const currentQuality = bitrateInfoList[quality];
                                        currentBitrate = parseFloat((currentQuality.bitrate / 1000000).toFixed(2));
                                        bandwidth = currentBitrate * 1.2; // Estimate bandwidth
                                    }
                                    
                                    // Get segment duration
                                    const dashMetrics = dashPlayer.getMetricsFor('video');
                                    if (dashMetrics && dashMetrics.SuggestedPresentationDelay) {
                                        segmentLength = dashMetrics.SuggestedPresentationDelay.toFixed(1);
                                    }
                                    
                                    // Get bandwidth estimate
                                    const bandwidthEstimate = dashPlayer.getBandwidthEstimate();
                                    if (bandwidthEstimate) {
                                        bandwidth = parseFloat((bandwidthEstimate / 1000000).toFixed(2));
                                    }
                                }
                            } catch (error) {
                                console.error('Error getting DASH metrics:', error);
                            }
                        }
                    }
                } catch (error) {
                    console.error('Error getting adaptive streaming metrics:', error);
                }
            }

            // Update stream info
            setStreamInfo({
                availableBitrates: allBitrates.length > 0 ? allBitrates.join(', ') + ' Mbps' : '–',
                availableResolutions: allResolutions.length > 0 ? allResolutions.join(', ') : '–',
                currentResolution: currentResolution,
                segmentLength: segmentLength + 's',
                fps: fps > 0 ? fps.toFixed(2) : '–',
                buffer: bufferTime + 's'
            });

            // Update playback metrics for charts
            const timestamp = new Date().toLocaleTimeString();
            setPlaybackMetrics(prev => {
                const newBandwidth = [...prev.bandwidth, bandwidth];
                const newBitrate = [...prev.currentBitrate, currentBitrate];
                const newFrameRate = [...prev.frameRate, fps];
                
                // Keep only last 20 data points
                return {
                    bandwidth: newBandwidth.slice(-20),
                    currentBitrate: newBitrate.slice(-20),
                    frameRate: newFrameRate.slice(-20)
                };
            });
            
            setChartLabels(prev => {
                const newLabels = [...prev, timestamp];
                return newLabels.slice(-20); // Keep only last 20 labels
            });
        } catch (error) {
            console.error('Error updating metrics:', error);
        }
    };
};

